/**
 * Seletor de Troncos de Saída para Chamadas Externas
 * VigosCloud - Incoming Call System
 * 
 * Implementa seleção específica para troncos de saída
 * Filtros: accountcode = tenant (apenas troncos do próprio tenant)
 */

import React, { useEffect, useState } from 'react';
import { getAvailableOutboundTrunks } from '../../../../services';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faServer, faSync, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

function TrunkSelectorOutbound({ 
  selectedTrunk, 
  onTrunkChange, 
  tenant, 
  error, 
  disabled = false,
  required = false 
}) {
  const [availableTrunks, setAvailableTrunks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Carregar troncos disponíveis quando o tenant mudar
  useEffect(() => {
    if (tenant) {
      loadAvailableTrunks();
    } else {
      setAvailableTrunks([]);
    }
  }, [tenant]);

  const loadAvailableTrunks = async () => {
    if (!tenant) return;
    
    try {
      setLoading(true);
      setLoadError(null);
      
      console.log(`🔍 [TRUNK-SELECTOR-OUTBOUND] Carregando troncos de saída para tenant: ${tenant}`);
      
      const response = await getAvailableOutboundTrunks(tenant);
      
      if (response?.success && response.data) {
        setAvailableTrunks(response.data);
        console.log(`✅ [TRUNK-SELECTOR-OUTBOUND] Carregados ${response.data.length} troncos de saída`);
        
        // Se não há troncos disponíveis, limpar seleção
        if (response.data.length === 0 && selectedTrunk) {
          onTrunkChange('');
        }
        
        // Se o tronco selecionado não está mais disponível, limpar seleção
        if (selectedTrunk && !response.data.find(trunk => trunk.name === selectedTrunk)) {
          console.log('⚠️ [TRUNK-SELECTOR-OUTBOUND] Tronco selecionado não está mais disponível, limpando seleção');
          onTrunkChange('');
        }
      } else {
        console.error('❌ [TRUNK-SELECTOR-OUTBOUND] Erro na resposta:', response);
        setLoadError(response?.error || 'Erro ao carregar troncos de saída');
        setAvailableTrunks([]);
      }
    } catch (error) {
      console.error('❌ [TRUNK-SELECTOR-OUTBOUND] Erro ao carregar troncos de saída:', error);
      setLoadError('Erro ao carregar troncos de saída disponíveis');
      setAvailableTrunks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTrunkChange = (e) => {
    const value = e.target.value;
    console.log(`🔍 [TRUNK-SELECTOR-OUTBOUND] Tronco de saída selecionado: ${value || 'Nenhum'}`);
    onTrunkChange(value);
  };

  const handleRefresh = () => {
    console.log('🔄 [TRUNK-SELECTOR-OUTBOUND] Atualizando lista de troncos de saída');
    loadAvailableTrunks();
  };

  return (
    <div className="field">
      <label className="label">
        <FontAwesomeIcon icon={faServer} className="mr-2" />
        Tronco de Saída
        {required && <span className="has-text-danger">*</span>}
      </label>
      
      <div className="field has-addons">
        <div className="control is-expanded">
          <div className={`select is-fullwidth ${error ? 'is-danger' : ''} ${loading ? 'is-loading' : ''}`}>
            <select 
              value={selectedTrunk || ''} 
              onChange={handleTrunkChange}
              disabled={disabled || loading}
            >
              <option value="">Selecione um tronco de saída{required ? '' : ' (opcional)'}</option>
              {availableTrunks.map(trunk => (
                <option key={trunk.name} value={trunk.name}>
                  {trunk.name} ({trunk.accountcode})
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="control">
          <button 
            type="button"
            className={`button ${loading ? 'is-loading' : ''}`}
            onClick={handleRefresh}
            disabled={disabled || loading}
            title="Atualizar lista de troncos"
          >
            <FontAwesomeIcon icon={faSync} />
          </button>
        </div>
      </div>
      
      {/* Mensagens de erro */}
      {error && (
        <p className="help is-danger">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
          {error}
        </p>
      )}
      
      {loadError && (
        <p className="help is-danger">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
          {loadError}
        </p>
      )}

      
      {/* Informações sobre critérios de seleção */}
      <div className="help">
        <strong>Critério de seleção:</strong> Troncos de saída exclusivos do seu tenant para chamadas externas.
        
        {!tenant && (
          <div className="has-text-warning mt-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
            Defina o cliente primeiro para carregar os troncos de saída disponíveis.
          </div>
        )}
        
        {tenant && availableTrunks.length === 0 && !loading && !loadError && (
          <div className="has-text-warning mt-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
            Nenhum tronco de saída disponível encontrado para o cliente {tenant}.
            <br />
            Verifique se existem troncos configurados com accountcode = {tenant}.
          </div>
        )}
        
        {availableTrunks.length > 0 && (
          <div className="has-text-success mt-2">
            ✅ {availableTrunks.length} tronco(s) de saída disponível(is) para o cliente {tenant}.
          </div>
        )}
      </div>
    </div>
  );
}

export default TrunkSelectorOutbound;
